import { ExternalLink } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { VoiceCallRating } from '@apps/web/components/outreaches/voice_call_rating';
import { Badge } from '@apps/web/components/ui/badge';
import { ScrollArea } from '@apps/web/components/ui/scroll_area';
import { Separator } from '@apps/web/components/ui/separator';
import {
    StatusBadge,
    type StatusConfig,
} from '@apps/web/components/ui/status_badge';
import { AutomatedVoiceQaSummary } from '@apps/web/components/voice_calls/automated_voice_qa_summary';
import { DeveloperTranscriptTimeline } from '@apps/web/components/voice_calls/developer_transcript_timeline';
import { LatencyPerTurnCard } from '@apps/web/components/voice_calls/latency_per_turn_card';
import { VoiceCallAudioPlayer } from '@apps/web/components/voice_calls/voice_call_audio_player';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { useVoiceCallRecordingUrls } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type AdminVoiceCallRead } from '@libraries/typescript/api_client';

// TODO: Sync this later
type DeveloperTimelineEntry = {
    readonly sequence: number;
    readonly timestamp_iso?: string;
    readonly pts_ns?: number;
    readonly kind: string;
    readonly role?: string;
    readonly text?: string;
};

// Define status configurations matching the pattern from contact-attempt-status-badge
const voiceCallStatusConfig: Record<string, StatusConfig> = {
    ONGOING: {
        label: 'Ongoing',
        intent: 'info',
    },
    ENDED: {
        label: 'Ended',
        intent: 'positive',
    },
    queued: {
        label: 'Queued',
        intent: 'neutral',
    },
    ringing: {
        label: 'Ringing',
        intent: 'info',
    },
    in_progress: {
        label: 'In Progress',
        intent: 'info',
    },
    ended: {
        label: 'Ended',
        intent: 'positive',
    },
    canceled: {
        label: 'Canceled',
        intent: 'neutral',
    },
    error: {
        label: 'Error',
        intent: 'negative',
    },
};

type VoiceCallDetailPaneProps = {
    readonly call: AdminVoiceCallRead | null;
    readonly initialTranscript?: string | null;
    readonly callId?: string | null;
};

export function VoiceCallDetailPane({
    call,
    initialTranscript,
    callId,
}: VoiceCallDetailPaneProps) {
    const organizationId = useOrganizationId();
    const [liveTranscript, setLiveTranscript] = useState(
        initialTranscript || null,
    );
    const [isRerunningQa, setIsRerunningQa] = useState(false);

    const voiceCallIdsForRecordings = useMemo(() => {
        if (!call?.recording_url) {
            return null;
        }
        const isS3Url =
            call.recording_url.includes('s3.amazonaws.com') ||
            call.recording_url.startsWith('s3://');
        return isS3Url ? [call.id] : null;
    }, [call]);

    const {
        data: recordingUrls,
        error: recordingUrlsError,
        isLoading: isRecordingUrlLoading,
    } = useVoiceCallRecordingUrls(organizationId, voiceCallIdsForRecordings);

    // Reset live data when call changes
    useEffect(() => {
        setLiveTranscript(initialTranscript || null);
    }, [call?.id, initialTranscript]);

    const handleRerunQa = useCallback(async () => {
        const voiceCallId = call?.id || callId;
        if (!voiceCallId) {
            return;
        }

        setIsRerunningQa(true);
        try {
            await getApiClient().post({
                url: '/admin/voice-calls/{voice_call_id}/agents/qa-rerun',
                pathParams: { voice_call_id: voiceCallId },
            });
            toast.success('Queued QA rerun', { persistNotification: false });
        } catch (error) {
            toast.error('Failed to queue QA rerun', {
                persistNotification: false,
            });
        } finally {
            setIsRerunningQa(false);
        }
    }, [call?.id, callId]);

    if (!call && !liveTranscript) {
        return (
            <div className="flex h-full items-center justify-center">
                <div className="text-center">
                    <h3 className="text-base font-medium">No call selected</h3>
                    <p className="text-muted-foreground mt-1 text-sm">
                        Select a call to view its transcript, recording, and
                        summary
                    </p>
                </div>
            </div>
        );
    }

    const currentStatus = call?.status || 'ONGOING';
    const currentTranscript = liveTranscript || call?.transcript;
    const developerTimeline =
        call?.developer_transcript as ReadonlyArray<DeveloperTimelineEntry> | null;
    const disconnectAttribution = call?.disconnect_attribution ?? null;
    const simulation = call?.simulation ?? null;

    const friendlyMetadataKey = (key: string) =>
        key
            .replace(/_/g, ' ')
            .replace(/\b([a-z])/g, (match) => match.toUpperCase());

    const callMetadataEntries = Object.entries(
        call?.call_metadata ?? {},
    ).filter(
        ([, value]) => value !== undefined && value !== null && value !== '',
    );

    return (
        <div className="flex h-full min-h-0 flex-col overflow-hidden">
            {call?.status && (
                <div className="flex flex-wrap items-center justify-end gap-2 border-b p-3">
                    {simulation ? (
                        <Badge variant="secondary">{simulation.label}</Badge>
                    ) : null}
                    <StatusBadge
                        status={
                            voiceCallStatusConfig[currentStatus] || {
                                label: currentStatus,
                                intent: 'neutral',
                            }
                        }
                    />
                </div>
            )}
            {simulation?.detail ? (
                <div className="border-b px-4 py-2">
                    <p className="text-muted-foreground text-xs">
                        {simulation.detail}
                    </p>
                </div>
            ) : null}
            {(call?.braintrust_run_url || call?.braintrust_run_id) && (
                <div className="border-b p-4">
                    <p className="mb-2 text-sm font-medium">Braintrust Run</p>
                    {call?.braintrust_run_url ? (
                        <a
                            href={call.braintrust_run_url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-sm font-medium underline underline-offset-2"
                        >
                            Open in Braintrust
                            <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                    ) : (
                        <code className="bg-muted rounded px-1 text-sm">
                            {call?.braintrust_run_id}
                        </code>
                    )}
                </div>
            )}
            {callMetadataEntries.length > 0 && (
                <div className="bg-muted/40 border-b p-4">
                    <p className="mb-3 text-sm font-medium">
                        Experiment metadata
                    </p>
                    <dl className="grid gap-2 text-xs">
                        {callMetadataEntries.map(([key, value]) => {
                            const formattedValue =
                                typeof value === 'object' && value !== null
                                    ? JSON.stringify(value, null, 2)
                                    : String(value);

                            return (
                                <div
                                    key={key}
                                    className="grid grid-cols-[140px_1fr] gap-3"
                                >
                                    <dt className="text-muted-foreground tracking-wide uppercase">
                                        {friendlyMetadataKey(key)}
                                    </dt>
                                    <dd className="text-foreground break-words">
                                        {typeof value === 'object' &&
                                        value !== null ? (
                                            <pre className="font-mono text-[11px] leading-snug whitespace-pre-wrap">
                                                {formattedValue}
                                            </pre>
                                        ) : (
                                            formattedValue
                                        )}
                                    </dd>
                                </div>
                            );
                        })}
                    </dl>
                </div>
            )}
            {(call?.id || callId) && (
                <div className="border-b p-4">
                    <AutomatedVoiceQaSummary
                        callId={call?.id || callId || ''}
                        callStatus={currentStatus}
                        isRerunning={isRerunningQa}
                        onRerun={handleRerunQa}
                    />
                </div>
            )}
            {(call?.id || callId) && (
                <div className="border-b p-4">
                    <VoiceCallRating voiceCallId={call?.id || callId || ''} />
                </div>
            )}
            {(call?.id || callId) && (
                <div className="border-b p-4">
                    <LatencyPerTurnCard callId={call?.id || callId || ''} />
                </div>
            )}
            {call?.recording_url && (
                <div className="border-b p-4">
                    <p className="mb-2 text-sm font-medium">Recording</p>
                    <VoiceCallAudioPlayer
                        voiceCall={call}
                        isRecordingUrlLoading={isRecordingUrlLoading}
                        prefetchedRecordingUrl={recordingUrls?.[call.id]}
                        recordingUrlError={recordingUrlsError}
                    />
                </div>
            )}
            {call?.call_summary && (
                <div className="border-b bg-blue-50 p-4 dark:bg-blue-950/30">
                    <p className="mb-2 text-sm font-medium">Call Summary</p>
                    <p className="text-foreground/80 text-sm">
                        {call.call_summary}
                    </p>
                </div>
            )}
            <ScrollArea className="min-h-0 flex-1">
                <div className="space-y-6 p-4">
                    <section>
                        <p className="mb-3 text-sm font-medium">Transcript</p>
                        {currentTranscript ? (
                            <div className="space-y-2">
                                {currentTranscript
                                    .split('\n')
                                    .filter((line) => line.trim() !== '')
                                    .map((line, index) => {
                                        const match = line.match(
                                            /^(Agent|User):\s*(.+)$/,
                                        );
                                        if (match) {
                                            const [, speaker, message] = match;
                                            return (
                                                <div
                                                    key={index}
                                                    className="flex gap-3 text-sm"
                                                >
                                                    <span className="min-w-[50px] font-medium">
                                                        {speaker}:
                                                    </span>
                                                    <span className="flex-1">
                                                        {message}
                                                    </span>
                                                </div>
                                            );
                                        }
                                        return (
                                            <div
                                                key={index}
                                                className="text-sm"
                                            >
                                                {line}
                                            </div>
                                        );
                                    })}
                            </div>
                        ) : (
                            <div className="text-muted-foreground rounded-md border border-dashed p-4 text-center text-sm">
                                No transcript available
                            </div>
                        )}
                    </section>
                    {(developerTimeline && developerTimeline.length > 0) ||
                    disconnectAttribution ? (
                        <section>
                            <Separator className="mb-4" />
                            <p className="mb-3 text-sm font-medium">
                                Developer Timeline
                            </p>
                            <DeveloperTranscriptTimeline
                                entries={developerTimeline ?? []}
                                disconnectAttribution={disconnectAttribution}
                                className="pb-2"
                            />
                        </section>
                    ) : null}
                </div>
            </ScrollArea>
        </div>
    );
}
